/**
 * Module dependencies.
 */

import { Faq, FaqProps } from 'src/types/faqs';
import { color, media, units } from 'src/styles/utils';
import { fetchFaq } from 'src/api/app/faqs/fetch-faq';
import { fetchPage } from 'src/api/app/pages/fetch-page';
import { getApiPageSlugByLocale } from 'src/core/utils/routes';
import { getHrefLangs } from 'src/core/utils/href-langs';
import { ifProp, theme } from 'styled-tools';
import { useRouter } from 'next/router';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import FaqDisclosure from 'src/components/faqs/faq-disclosure';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import React, { ReactElement } from 'react';
import SchemaFaq from 'src/components/schema-faq';
import Type from 'src/components/core/typography';
import get from 'lodash/get';
import map from 'lodash/map';
import styled, { css } from 'styled-components';
import useFaq from 'src/api/app/faqs/use-faq';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import usePage from 'src/api/app/pages/use-page';

/**
 * `PageProps` type.
 */

type PageProps = {
  id: string,
  slug: string,
  title: string,
  translations: Record<string, unknown>[]
};

/**
 * `Props` type.
 */

type Props = {
  data: PageProps,
  faq: FaqProps,
  slug: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section<{ isLoading: boolean }>`
  margin-bottom: ${units(14)};
  padding-top: ${theme('dimensions.navbarHeightMobile')}px;

  ${media.min('ms')`
    margin-bottom: ${units(25)};
    padding-top: ${theme('dimensions.navbarHeight')}px;
  `};

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  padding-top: ${units(5)};

  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. title title . .'
      '. .     faq  . .';
    grid-template-columns: 1fr 1fr 9fr 1fr 1fr;
    padding-top: ${units(6)};
  `}

  ${media.min('md')`
    grid-template-columns: 1fr 2fr 9fr 2fr 1fr;
  `}
`;

/**
 * `FaqLabel` styled component.
 */

const FaqLabel = styled(Type.Paragraph)`
  color: ${color('brown400')};
  display: block;
  font-style: italic;
  margin-bottom: ${units(1)};
  text-transform: uppercase;

  ${media.min('ms')`
    margin-bottom: ${units(2)};
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  grid-area: title;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  margin-bottom: ${units(5)};

  ${media.min('ms')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `FaqsDisclosureWrapper` styled component.
 */

const FaqsDisclosureWrapper = styled.div`
  border-top: 1px solid ${color('grey100')};
  color: ${color('grey400')};
  font-size: ${units(2)};
  grid-area: faq;
  line-height: ${units(4)};
`;

/**
 * `FaqsDetails` page.
 */

const FaqsDetails = (props: Props): ReactElement => {
  const { query } = useRouter();
  const {
    data: pageData,
    isLoading: isLoadingPage,
    isSuccess: isSuccessPage
  } = usePage<PageProps>({
    initialData: props.data,
    slug: props.slug
  });

  const {
    data: faqData,
    isLoading: isLoadingFaq,
    isSuccess: isSuccessFaq
  } = useFaq<FaqProps>({
    initialData: props?.faq,
    slug: get(query, 'slug')
  });

  const descriptionSeo = faqData?.descriptionSeo;
  const subtitle = faqData?.subtitle ?? pageData?.title;
  const title = faqData?.name;
  const titleSeo = faqData?.titleSeo;
  const isLoading = isLoadingPage || isLoadingFaq;
  const isSuccess = isSuccessPage && isSuccessFaq;

  useNavbarTheme({ theme: 'dark' });

  return (
    <>
      <Metatags
        description={descriptionSeo}
        hrefLangs={getHrefLangs(faqData?.translations, 'faq')}
        pageTitle={titleSeo || title}
        title={titleSeo || title}
      />

      <SchemaFaq faqs={faqData?.faqs || []} />

      <Section isLoading={isLoading}>
        <Loading active={isLoading} />

        {isSuccess && (
          <Container>
            <Grid>
              <TitleWrapper>
                <Title as={'h1'}>
                  <FadeInUpAnimation>
                    <FaqLabel as={'span'}>
                      {`${subtitle} `}
                    </FaqLabel>
                  </FadeInUpAnimation>

                  <FadeInUpAnimation>
                    {title}
                  </FadeInUpAnimation>
                </Title>
              </TitleWrapper>

              <FaqsDisclosureWrapper>
                {map(faqData?.faqs, ({ id, ...rest }: Faq) => (
                  <FadeInUpAnimation key={id}>
                    <FaqDisclosure {...rest} />
                  </FadeInUpAnimation>
                ))}
              </FaqsDisclosureWrapper>
            </Grid>
          </Container>
        )}
      </Section>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export async function getServerSideProps({ locale, params, query }) {
  const slug = getApiPageSlugByLocale('faqs', locale);
  const faq = await fetchFaq({ locale, slug: params?.slug });
  const pageData = await fetchPage({
    hasPreview: query?.preview,
    locale,
    slug
  });

  if (!faq) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      data: pageData,
      faq,
      slug
    }
  };
}

/**
 * Export `FaqsDetails` page.
 */

export default FaqsDetails;
